import Image from 'next/image';
import { Button } from '../../components/Button';
import Link from 'next/link';

import Head from 'next/head';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/autoplay';

import { motion } from 'framer-motion';

// Data
import {
  designServicesCardData,
  whyChooseUsData,
  industriesWeServeData,
  toolsWeUseData,
  projectsWehaveDoneData,
  otherServices,
} from '../../data/innerPageData/designServicesData';

import { DesignProcess } from '../../components/DesignProcess';
import { VisuallyHidden } from '@chakra-ui/react';

const imageLoader = require('../../loader');

export const getStaticProps = async () => {
  return {
    props: {
      designServicesCardData,
      whyChooseUsData,
      industriesWeServeData,
      toolsWeUseData,
      projectsWehaveDoneData,
      otherServices,
    },
  };
};

const toolVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const toolItemVariants = {
  hidden: {
    scale: 0,
  },
  visible: {
    scale: 1,
  },
};

export default function DesignServices({
  designServicesCardData,
  whyChooseUsData,
  industriesWeServeData,
  toolsWeUseData,
  projectsWehaveDoneData,
  otherServices,
}) {
  return (
    <>
      <Head>
        <title>Design Services | drive</title>
      </Head>
      <section className="inner-page-hero servicesHero">
        <div className="section-wrapper h-100 flex-column justify-center">
          <h1>Design Services</h1>
          <p>
            Support your customer’s journey by aligning and optimizing your
            designs with drive
          </p>
          <div>
            <a href="#inner-content-2" className="btn-with-arrow-light-down">
              Explore More
            </a>
          </div>
        </div>
        <div className="servicesHero-overlay"></div>
      </section>

      <section className="services-inner-section" id="inner-content-2">
        <div className="section-wrapper flex-column">
          <div className="services-inner-section-text">
            <div className="services-inner-section-text-left">
              <h1>A Full Service Design Agency</h1>
              <p>
                Using the right web designs can make or break a company’s
                success. At the same time, it can be challenging to find a
                high-quality web design service to meet your needs. That’s where
                we come in!
              </p>
              <p>
                So, how can our branding and creative design firm assist you?
                Our professionals at drive have a wide range of design services
                to help your business thrive!
              </p>
            </div>
            <div className="services-inner-section-text-right">
              <div className="cta-wrapper">
                <p>Start a Conversation</p>
                <Button
                  linkTo={'/contact-us'}
                  title={'Talk to an Expert'}
                  className={'btn glowing-border'}
                />
              </div>
            </div>
          </div>
          <div className="services-inner-section-image">
            <Image
              objectFit="cover"
              height={450}
              width={1200}
              src={'/Design-Services@2x-2048x1037.png'}
              alt="Design Services"
            />
          </div>
        </div>
      </section>

      <section className="services-inner-section blue">
        <div className="section-wrapper flex-column">
          <div className="services-inner-section-text-only">
            <h1>Specialized Design Services</h1>
            <p>
              Your company’s success can hinge on its image. When you work with
              our experts, you’ll be working one-on-one with the best designers
              for unique visuals made solely for your business.
            </p>
          </div>
          <div className="flex justify-between">
            <div className="services-inner-section-info-card">
              <div className="services-inner-section-info-card-icon">
                <Image
                  width={80}
                  height={80}
                  src={'/design_icon-1.png'}
                  alt="design services"
                />
              </div>
              <div className="services-inner-section-info-card-text">
                <h2> UI/UX Design </h2>
                <p>
                  We know how costly a bad user experience can be. Users expect
                  a frictionless online{' '}
                  <Link href="/services/ui-ux">
                    <a>UI/UX</a>
                  </Link>{' '}
                  website, and we’ll help you give them what they want!
                </p>
              </div>
              <div className="services-inner-section-info-card-footer">
                <Button
                  linkTo={'/services/ui-ux'}
                  className={'btn-with-arrow'}
                  title={'Our UI/UX Services'}
                />
              </div>
            </div>
            <div className="services-inner-section-info-card">
              <div className="services-inner-section-info-card-icon">
                <Image
                  width={80}
                  height={80}
                  objectFit="contain"
                  src={'/design_icon-2.png'}
                  alt="design services"
                />
              </div>
              <div className="services-inner-section-info-card-text">
                <h2>Brand & Visual Design</h2>
                <p>
                  drive offers the best corporate{' '}
                  <Link href="/services/brand-visual-design/">
                    <a>branding and visual design</a>
                  </Link>{' '}
                  services. Our expert digital designers will ensure that your
                  brand speaks directly to your customer and clearly reflects
                  your business.
                </p>
              </div>
              <div className="services-inner-section-info-card-footer">
                <Button
                  linkTo={'/services/ui-ux'}
                  className={'btn-with-arrow'}
                  title={'Our Branding Services'}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="section-wrapper flex-column">
          <h1>Our Design Services</h1>
          <div className="flex justify-between">
            {designServicesCardData.map(({ icon, title }, index) => (
              <div className="inner-page-service-card" key={index}>
                <div className="inner-page-service-card-icon">
                  <Image
                    width={90}
                    height={90}
                    objectFit="contain"
                    src={`/${icon}`}
                    alt={title}
                  />
                </div>
                <div className="inner-page-service-card-text">
                  <h3>{title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="why-choose-drive">
        <div className="section-wrapper flex-column">
          <h1>Why Most Business Leaders Choose drive</h1>
          <p>
            We appreciate that you have a choice when it comes to partnering
            with a digital agency. 8 of 10 business leaders prefer drive
          </p>
          <div className="mt-2 mb-2">
            <Button
              linkTo={'/contact-us'}
              title={'Talk to an Expert'}
              className={'btn-with-arrow-light'}
            />
          </div>

          {whyChooseUsData && (
            <div className="why-choose-drive-card-wrapper">
              {whyChooseUsData.map(({ icon, title, text }, index) => (
                <div className="why-choose-drive-card" key={index}>
                  <div className="why-choose-drive-card-icon">
                    <Image
                      width={90}
                      height={90}
                      objectFit="contain"
                      objectPosition="center"
                      src={`/${icon}`}
                      alt={title}
                    />
                  </div>
                  <div className="why-choose-drive-card-text">
                    <h2>{title}</h2>
                    <p>{text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <DesignProcess />

      {toolsWeUseData && (
        <section className="tools-we-use">
          <div className="section-wrapper flex-column justify-center align-center">
            <h1>Tools for Creativity and Collaboration</h1>
            <p>
              We use professional tools for immersive sketching, brainstorming,
              and collaboration around your product idea.
            </p>

            <motion.div
              className="tools-container mt-2"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={toolVariants}
            >
              {toolsWeUseData.map(({ icon, alt }, index) => (
                <motion.div
                  key={index}
                  className="tool-icon"
                  variants={toolItemVariants}
                >
                  <Image width={60} height={60} src={`/${icon}`} alt={alt} />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {industriesWeServeData && (
        <section className="industries-we-serve">
          <div className="section-wrapper flex-column">
            <h1>Industries We Serve</h1>
            <p>
              Looking for more phone calls, form submissions, sales, and brand
              engagement? Our experience includes work with product and service
              companies that serve B2B and B2C customers, as well as non-profit
              organizations.
            </p>

            <div className="industries-we-serve-slide">
              <Swiper
                modules={[Autoplay]}
                autoplay={{
                  delay: 3000,
                  disableOnInteraction: false,
                }}
                spaceBetween={20}
                breakpoints={{
                  640: {
                    slidesPerView: 2,
                  },
                  768: {
                    slidesPerView: 3,
                  },
                  1200: {
                    slidesPerView: 4,
                  },
                }}
              >
                {industriesWeServeData.map(({ url, title }, index) => (
                  <SwiperSlide key={index}>
                    <div className="industries-slide-card">
                      <Image
                        width={400}
                        height={200}
                        objectFit="cover"
                        src={`/${url}`}
                        alt={title}
                      />
                      <h1>{title}</h1>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </section>
      )}

      {projectsWehaveDoneData && (
        <section className="projects-we-have-done">
          <div className="section-wrapper flex-column justify-center align-center">
            <h1>Amazing Web Design Projects We Have Done</h1>
            <div className="projects-we-have-done-slide">
              <Swiper
                modules={[Navigation]}
                navigation
                spaceBetween={20}
                breakpoints={{
                  640: {
                    slidesPerView: 2,
                  },
                  768: {
                    slidesPerView: 3,
                  },
                  1200: {
                    slidesPerView: 4,
                  },
                }}
              >
                {projectsWehaveDoneData.map(
                  ({ url, title, services }, index) => (
                    <SwiperSlide key={index}>
                      <div className="projects-slide-card">
                        <Image
                          width={400}
                          height={300}
                          objectFit="cover"
                          src={`/${url}`}
                          alt={title}
                        />
                        <h1>{title}</h1>
                        <p>{services}</p>
                      </div>
                    </SwiperSlide>
                  )
                )}
              </Swiper>
            </div>
          </div>
        </section>
      )}

      {otherServices && (
        <section className="other-services">
          <div className="section-wrapper flex-column">
            <h1>Other Services</h1>
            <div className="other-services-image-wrapper">
              {otherServices.map(({ link, imageUrl, title }, index) => (
                <div className="other-services-image" key={index}>
                  <Link href={link}>
                    <a>
                      <Image
                        objectFit="cover"
                        width={600}
                        height={400}
                        src={`/${imageUrl}`}
                        alt={title}
                      />
                      <h2>{title}</h2>
                    </a>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
